import { Controller } from "@hotwired/stimulus"

export default class extends Controller<HTMLElement> {
  static targets = ["query", "item", "radio", "badge", "empty", "prompt", "summaryName", "summaryInfo"]

  declare readonly queryTarget: HTMLInputElement
  declare readonly hasItemTarget: boolean
  declare readonly itemTargets: HTMLElement[]
  declare readonly radioTargets: HTMLInputElement[]
  declare readonly badgeTargets: HTMLElement[]
  declare readonly hasEmptyTarget: boolean
  declare readonly emptyTarget: HTMLElement
  declare readonly hasPromptTarget: boolean
  declare readonly promptTarget: HTMLElement
  declare readonly hasSummaryNameTarget: boolean
  declare readonly summaryNameTarget: HTMLInputElement
  declare readonly hasSummaryInfoTarget: boolean
  declare readonly summaryInfoTarget: HTMLInputElement

  connect() {
    if (this.hasEmptyTarget && this.hasItemTarget && this.queryTarget.value.trim() !== "") this.filter()
  }

  filter() {
    if (!this.hasEmptyTarget || !this.hasItemTarget) return

    const query = this.queryTarget.value.trim().toLocaleLowerCase()
    if (query === "") {
      const selectedItem = this.itemTargets.find((item) =>
        item.querySelector("[data-property-filter-target~='radio']:checked")
      )
      this.itemTargets.forEach((item) => { item.classList.toggle("hidden", item !== selectedItem) })
      const hasNoItems = this.itemTargets.length === 0
      this.emptyTarget.classList.toggle("hidden", !hasNoItems)
      if (this.hasPromptTarget) this.promptTarget.classList.toggle("hidden", Boolean(selectedItem) || hasNoItems)
      return
    }
    if (this.hasPromptTarget) this.promptTarget.classList.add("hidden")

    let visibleCount = 0
    let selectedItemHidden = false

    this.itemTargets.forEach((item) => {
      const visible = (item.dataset.propertyFilterSearchableValue || "").toLocaleLowerCase().includes(query)
      item.classList.toggle("hidden", !visible)
      if (visible) visibleCount += 1
      if (!visible && item.querySelector("[data-property-filter-target~='radio']:checked")) {
        selectedItemHidden = true
      }
    })
    if (selectedItemHidden) this.clearSelection()
    this.emptyTarget.classList.toggle("hidden", visibleCount > 0)
  }

  select(event: Event) {
    const item = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-property-filter-target~='item']")
    if (!item) return

    const radio = item.querySelector<HTMLInputElement>("[data-property-filter-target~='radio']")
    if (radio) radio.checked = true

    this.itemTargets.forEach((candidate, index) => {
      const selected = candidate === item
      candidate.classList.toggle("bg-primary/5", selected)
      this.badgeTargets[index].classList.toggle("hidden", !selected)
    })
    this.writeSummary(item.dataset.propertyFilterNameValue || "", item.dataset.propertyFilterInfoValue || "")
    this.collapse(item)
  }

  // 選択したら候補リストを畳んで選択中の1件だけ残す。検索語は残したままなので、
  // 検索欄をクリックすれば同じ条件で開き直せる（filter が選択状態を保つ）
  private collapse(selectedItem: HTMLElement) {
    this.itemTargets.forEach((item) => { item.classList.toggle("hidden", item !== selectedItem) })
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
    if (this.hasPromptTarget) this.promptTarget.classList.add("hidden")
  }

  // キャンセルは物件選択の取り消しにとどめ、未選択のとき（＝取り消すものが無い）だけ
  // 本来のリンク遷移でフォームを離れる。物件選択ステップが隠れている間も遷移に任せる
  cancel(event: Event) {
    if (this.queryTarget.offsetParent === null) return
    if (!this.radioTargets.some((radio) => radio.checked)) return

    event.preventDefault()
    this.clearSelection()
    this.queryTarget.value = ""
    this.filter()
  }

  clearSelection() {
    this.radioTargets.forEach((radio) => { radio.checked = false })
    this.itemTargets.forEach((item) => { item.classList.remove("bg-primary/5") })
    this.badgeTargets.forEach((badge) => { badge.classList.add("hidden") })
    this.writeSummary("", "")
  }

  // 確認ステップへ選択内容を引き継ぐための hidden。確認ステップを持たない画面
  // （OCR登録の物件選択）では描かれないため、ターゲットの有無を確かめてから触る
  private writeSummary(name: string, info: string) {
    if (this.hasSummaryNameTarget) this.summaryNameTarget.value = name
    if (this.hasSummaryInfoTarget) this.summaryInfoTarget.value = info
  }
}
